import React, { useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import InputSearch from "../components/InputForm";
import NewSearchSection from "../components/NewSearchSection";
import HelpSection from "../components/HelpSection";
import BusinessInfoCard from "../components/BusinessInfoCard";
import NavBar from "../components/Navbar";
import googleReviewImage from "../assets/google-avis-client-suisse-romande-marketlocal.png";
import arrowTrendingImage from "../assets/arrow-trending-svgrepo-com.svg";
import "../styles/App.css";

function HomeContainer() {
  // État pour afficher ou masquer la section d'aide
  const [showHelpSection, setShowHelpSection] = useState(false);
  
  // État pour afficher le texte pour effectuer une nouvelle recherche
  const [showNewSearch, setShowNewSearch] = useState(false);
  
  // État pour gérer la valeur de l'input de recherche
  const [inputValue, setInputValue] = useState("");
  
  // État pour gérer la liste filtrée des entreprises issues de la recherche
  const [filtered, setFiltered] = useState([]);
  
  // État pour gérer les informations de l'entreprise sélectionnée
  const [businessInfo, setBusinessInfo] = useState(null);
  
  // État pour gérer l'animation de chargement
  const [loading, setLoading] = useState(false);
  
  // État pour l'affichage des erreurs dans le frontend
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  
  // État pour gérer la limite de de génération de lien
  const [totalLinks, setTotalLinks] = useState(0);
  
  // Fonction pour gérer la sélection d'un lieu à partir du champ de recherche
  const handleSelectPlace = async (place) => {
    try {
      const { name, formatted_address } = place;
      // Début du chargement
      setLoading(true);
      // Requête vers l'API de recherche
      const response = await axios.post(
        "https://aura-hs4v.onrender.com/search", // Remplacez par votre URL si nécessaire
        { name, address: formatted_address },
        { withCredentials: true }, // Important pour envoyer les cookies
      );
      
      const company = response.data;
      setFiltered([company]);
      setBusinessInfo(company);
      
      // Fin du chargement
      setLoading(false);
      
      // Afficher la section de nouvelle recherche
      setShowNewSearch(true);
    } catch (error) {
      console.error(
        "Error searching company:",
        error.response?.data || error.message || error,
      );
      
      setErrorMessage("Une erreur s'est produite");
      // setErrorMessage(error.response?.data || "Une erreur s'est produite");
      setShowError(true);
      setLoading(false);
    }
  };
  // Fonction pour afficher ou masquer la section d'aide
  const toggleHelpSection = () => {
    setShowHelpSection((prev) => !prev);
  };
  // Fonction pour afficher le bouton pour une nouvelle recherche
  const toggleNewSearchSection = () => {
    setShowNewSearch(false);
    setBusinessInfo(null); // Réinitialiser les informations de l'entreprise
    setInputValue(""); // Réinitialiser la valeur de l'input
    setFiltered([]); // Réinitialiser les résultats filtrés
  };

  // Fonction pour gérer la sélection d'une entreprise à partir de la liste filtrée
  const handleSelectBusiness = (business) => {
    setBusinessInfo(business);
    setInputValue(business.name);
    setFiltered([]);
  };

  // Rendu de la structure de la page
  return (
    <div className="min-h-screen bg-almostWhite flex flex-col">
      <NavBar />
      {showError && (
        <div
          id="toast-warning"
          className="flex items-center w-full max-w-xs p-4 text-gray-500 bg-white rounded-lg shadow dark:text-gray-400 dark:bg-gray-800 absolute top-4 right-4"
          role="alert"
        >
          <div className="inline-flex items-center justify-center flex-shrink-0 w-8 h-8 text-orange-500 bg-orange-100 rounded-lg dark:bg-orange-700 dark:text-orange-200">
            <svg
              className="w-5 h-5"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM10 15a1 1 0 1 1 0-2 1 1 0 0 1 0 2Zm1-4a1 1 0 0 1-2 0V6a1 1 0 0 1 2 0v5Z" />
            </svg>
            <span className="sr-only">Icône d'avertissement</span>
          </div>
          <div className="ms-3 text-sm font-normal">{errorMessage}</div>
          <button
            type="button"
            className="ms-auto -mx-1.5 -my-1.5 bg-white text-gray-400 hover:text-gray-900 rounded-lg focus:ring-2 focus:ring-gray-300 p-1.5 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8 dark:text-gray-500 dark:hover:text-white dark:bg-gray-800 dark:hover:bg-gray-700"
            aria-label="Close"
            onClick={() => setShowError(false)}
          >
            <span className="sr-only">Fermer</span>
            <svg
              className="w-3 h-3"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 14 14"
            >
              <path
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
              />
            </svg>
          </button>
        </div>
      )}
      <div className="flex-grow flex flex-col justify-center items-center py-8">
        <div className="w-full max-w-3xl px-4 md:px-8 lg:px-12 mx-auto text-center">
          <div className="logo-wrapper mb-6">
            <img
              src={googleReviewImage}
              alt="Google avis client Suisse Romande Marketlocal.ch"
              id="google-review-image"
            />
          </div>
          <h1 className="text-3xl sm:text-3xl md:text-3xl lg:text-4xl xl:text-4xl 2xl:text-4xl font-bold mb-4">
            Obtenez votre lien d'évaluation Google
          </h1>
          <p className="text-lg px-4 md:px-8 lg:px-12 mb-6">
            Obtenez plus d'avis Google en Suisse Romande avec notre générateur
            de lien d'évaluation gratuit. Partagez{" "}
            <span className="font-semibold text-zap-orange">l'URL</span> ou le
            <span className="font-semibold text-zap-orange"> QR code</span>{" "}
            directement avec vos clients.
          </p>
          <img
            Expand
            Down
            src={arrowTrendingImage}
            alt="Follow instructions"
            id="follow-instruction"
            className="mx-auto my-4"
          />
          {showNewSearch ? (
            <NewSearchSection toggleNewSearchSection={toggleNewSearchSection} />
          ) : (
            <InputSearch
              handleSelectPlace={handleSelectPlace}
              toggleHelpSection={toggleHelpSection}
              value={inputValue}
              setValue={setInputValue}
            />
          )}
          {loading ? (
            <button
              disabled
              type="button"
              className="mt-6 py-2.5 px-5 me-2 text-sm font-medium text-gray-900 inline-flex items-center"
            >
              <svg
                aria-hidden="true"
                role="status"
                className="inline w-4 h-4 me-3 text-gray-200 animate-spin dark:text-gray-600"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="#FF4F00"
                />
              </svg>
              Chargement...
            </button>
          ) : (
            businessInfo && <BusinessInfoCard business={businessInfo} />
          )}
          {showHelpSection && <HelpSection />}
        </div>
      </div>
    </div>
  );
}
export default HomeContainer;
